import { defineStore } from "pinia";
import { ref } from "vue";

export interface GroupPlacement {
  team: string;
  place: number;
  points: number;
  wins: number;
  losses: number;
  draws: number;
  goalsScored: number;
  goalsAgainst: number;
}

export interface Group {
  id: string;
  name: string;
  placements: GroupPlacement[];
  fixed: boolean;
}

export const useGroupStore = defineStore("group", () => {
  const groups = ref<Group[]>([]);
  const loading = ref(false);
  const error = ref<null | string>(null);

  async function fetchAll() {
    loading.value = true;
    error.value = null;
    try {
      const response = await fetch("/api/groups");
      if (!response.ok) {
        throw new Error("Failed to fetch groups");
      }
      groups.value = await response.json() as Group[];
    } catch (err) {
      error.value = "An error occurred while fetching groups";
    } finally {
      loading.value = false;
    }
  }

  async function put(group: Omit<Group, "id"> & { id?: string }) {
    loading.value = true;
    error.value = null;
    try {
      const response = await fetch("/api/groups", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(group),
      });
      if (!response.ok) {
        throw new Error("Failed to save group");
      }
      await fetchAll(); // Refresh the list after saving
    } catch (err) {
      error.value = "An error occurred while saving the group";
    } finally {
      loading.value = false;
    }
  }

  async function remove(group: Group) {
    loading.value = true;
    error.value = null;
    try {
      const response = await fetch(`/api/groups/${group.id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Failed to delete group");
      }
      await fetchAll(); // Refresh the list after deletion
    } catch (err) {
      error.value = "An error occurred while deleting the group";
    } finally {
      loading.value = false;
    }
  }

  // Initial fetch of groups when the store is created
  fetchAll().then();

  return { groups, loading, error, put, remove, fetchAll };
})
